import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useTheme } from '@mui/material/styles';
import type { SvgIconComponent } from '@mui/icons-material';

type KpiColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info';

interface KpiCardProps {
  title: string;
  value: string;
  sub?: string;
  icon: SvgIconComponent;
  color?: KpiColor;
  trendValue?: number;
  trendLabel?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({
  title,
  value,
  sub,
  icon: Icon,
  color = 'primary',
  trendValue,
  trendLabel = 'vs last period',
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const iconColor = theme.palette[color].main;
  const iconBg = isDark ? `${iconColor}22` : `${iconColor}15`;

  const isUp = (trendValue ?? 0) >= 0;
  const trendColor = isUp
    ? theme.palette.error.main
    : theme.palette.success.main;
  const trendBg = isDark ? `${trendColor}22` : `${trendColor}15`;

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 185, // Enforces uniformity across the grid
        boxShadow:
          '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          p: 2,
          '&:last-child': { pb: 2 },
        }}
      >
        {/* HEADER BLOCK */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1.5,
          }}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ lineHeight: 1.2, display: 'block' }}
          >
            {title}
          </Typography>

          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              bgcolor: iconBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 16, color: iconColor }} />
          </Box>
        </Box>

        {/* VALUE BLOCK */}
        <Typography
          variant="h4"
          sx={{ mb: 0.5, fontSize: '1.75rem', fontWeight: 700 }}
        >
          {value}
        </Typography>

        {/* SUBTITLE BLOCK - FIXED MINIMUM HEIGHT ENFORCES GRID ALIGNMENTS */}
        <Box sx={{ minHeight: 18, mb: 'auto' }}>
          {sub && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block' }}
            >
              {sub}
            </Typography>
          )}
        </Box>

        {/* TREND BLOCK - FIXED MINIMUM HEIGHT ENFORCES GRID ALIGNMENTS */}
        <Box
          sx={{
            minHeight: 38,
            mt: 1.5,
            pt: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          {trendValue !== undefined && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: '6px',
                bgcolor: trendBg,
              }}
            >
              {isUp ? (
                <TrendingUpIcon sx={{ fontSize: 13, color: trendColor }} />
              ) : (
                <TrendingDownIcon sx={{ fontSize: 13, color: trendColor }} />
              )}
              <Typography
                variant="caption"
                sx={{ fontSize: 11, fontWeight: 600, color: trendColor }}
              >
                {isUp ? '+' : ''}
                {trendValue.toFixed(1)}% {trendLabel}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default KpiCard;
